import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { EmspUserInvoiceBaseModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/invoices-service-lib/src/wrapper/outputs/emsp-user-invoice-base.model';
import { CellContext } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useLazyEmspBoGetInvoicePdfQuery } from '@app/core/service/emsp-bo-invoice.service';
import { downloadEmspBoInvoicePdf } from '@app/feature/emsp-bo-invoice/emsp-bo-invoice-pdf.helper';

export interface EmspBoInvoiceListActionsCellProps
  extends CellContext<EmspUserInvoiceBaseModel, unknown> {}

export const EmspBoInvoiceListActionsCell = (
  props: EmspBoInvoiceListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canRead = safeHasAccessTo(userPermissions, {
    resourceName: Resource.EMSP_BO,
    action: ActionsByResource.EMSP_BO.READ_INVOICE,
  });

  const canDownloadPdf = safeHasAccessTo(userPermissions, {
    resourceName: Resource.EMSP_BO,
    action: ActionsByResource.EMSP_BO.DOWNLOAD_INVOICE_PDF,
  });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [lazyGetInvoicePdfQuery] = useLazyEmspBoGetInvoicePdfQuery();

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleDownloadInvoicePdf = async (): Promise<void> => {
    try {
      const invoice = row.original;

      const { isError, isSuccess, data, error } = await lazyGetInvoicePdfQuery({
        invoiceId: invoice.id,
      });

      if (isError) {
        throw new Error(JSON.stringify(error));
      }

      if (isSuccess) {
        await downloadEmspBoInvoicePdf(invoice, data);
        toast.success(t('$.emsp-bo-invoice-download-pdf.success'));
      }
    } catch (error: unknown) {
      toast.error(
        t('$.emsp-bo-invoice-download-pdf.error', {
          error: String(error),
        }),
      );
    }
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end">
      {!canRead && <div />}
      {canRead && (
        <ButtonIcon
          color={ButtonColor.Ghost}
          icon="eye"
          onClick={() =>
            navigate(
              generatePath(APP_PATH.EMSP_BO_INVOICE.DETAILS, {
                id: row.original.id,
              }),
            )
          }
          className={cssTable.displayOnHover}
        />
      )}
      {canDownloadPdf && (
        <ButtonIcon
          icon="download-01"
          color={ButtonColor.Ghost}
          onClick={handleDownloadInvoicePdf}
          className={cssTable.displayOnHover}
        />
      )}
    </Stack>
  );
};
